import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Alert, Chip, FormControl, InputLabel, Select, MenuItem, Grid, Card, CardContent, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider, Checkbox, FormControlLabel, Collapse, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { fetchJson } from '../../utils/fetchJson';
import ChildPicker from '../../components/Parent/ChildPicker';

// Instalment status is derived server-side (see utils/installmentAllocation).
const INSTALLMENT_STATUS_COLOR = {
  paid: 'success',
  partial: 'info',
  overdue: 'error',
  unpaid: 'default',
};

const INSTALLMENT_STATUS_FALLBACK = {
  paid: 'Paid',
  partial: 'Part paid',
  overdue: 'Overdue',
  unpaid: 'Not paid',
};

export default function ParentFeesPaymentsPage() {
  const { t } = useTranslation();
  const [expandedFees, setExpandedFees] = useState({});
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [feeStructures, setFeeStructures] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ totalExpected: 0, totalPaid: 0, outstanding: 0 });
  const [error, setError] = useState(null);

  const [payOpen, setPayOpen] = useState(false);
  const [payFeeStructureId, setPayFeeStructureId] = useState('');
  const [payGateway, setPayGateway] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [payMsisdn, setPayMsisdn] = useState('');
  const [payEmail, setPayEmail] = useState('');
  const [payBankRef, setPayBankRef] = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [refreshBusyId, setRefreshBusyId] = useState(null);
  const [receiptBusyId, setReceiptBusyId] = useState(null);
  const [methodAvailability, setMethodAvailability] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);

  // Offline receipt submission (pay one or more fees by bank / mobile money + upload proof)
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptItems, setReceiptItems] = useState([]); // [{ feeStructureId, name, outstanding, selected, amount }]
  const [receiptPayer, setReceiptPayer] = useState('');
  const [receiptRef, setReceiptRef] = useState('');
  const [receiptNotes, setReceiptNotes] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptBusy, setReceiptBusy] = useState(false);

  const pickDefaultGateway = (avail) => {
    if (avail?.mtn) return 'mtn';
    if (avail?.stripe) return 'stripe';
    if (avail?.orange) return 'orange';
    if (avail?.bank) return 'bank';
    return '';
  };

  const visibleGateways = (() => {
    if (!methodAvailability) return [];
    const out = [];
    if (methodAvailability.mtn) out.push('mtn');
    if (methodAvailability.stripe) out.push('stripe');
    if (methodAvailability.orange) out.push('orange');
    if (methodAvailability.bank) out.push('bank');
    return out;
  })();

  const loadFees = async (childId) => {
    const data = await fetchJson(`/api/portal/parent/child/${encodeURIComponent(childId)}/fees`);
    setFeeStructures(Array.isArray(data?.feeStructures) ? data.feeStructures : []);
    setPayments(Array.isArray(data?.payments) ? data.payments : []);
    setInvoices(Array.isArray(data?.invoices) ? data.invoices : []);
    setSummary(data?.summary || { totalExpected: 0, totalPaid: 0, outstanding: 0 });
  };

  const getFeePaidAmount = (feeStructureId) => {
    const id = String(feeStructureId);
    return (payments || [])
      .filter(p => String(p.feeStructureId) === id && String(p.status || '').toLowerCase() === 'completed')
      .reduce((sum, p) => sum + Number(p.amount || 0), 0);
  };

  const getFeeOutstanding = (fs) => {
    const required = Number(fs?.amount || 0);
    const paid = getFeePaidAmount(fs?.id);
    return Math.max(required - paid, 0);
  };

  const openPay = (fs) => {
    setError(null);
    setPayFeeStructureId(fs?.id ? String(fs.id) : '');
    setPayGateway(pickDefaultGateway(methodAvailability));
    setPayMsisdn('');
    setPayEmail('');
    setPayBankRef('');
    const outstanding = fs ? getFeeOutstanding(fs) : 0;
    setPayAmount(outstanding > 0 ? String(outstanding) : '');
    setPayOpen(true);
  };

  const submitPay = async () => {
    if (!selectedChild) return;
    const feeStructureIdNum = Number(payFeeStructureId);
    if (!Number.isFinite(feeStructureIdNum)) {
      setError(t('parentFees.errors.selectFeeItem'));
      return;
    }
    const gateway = String(payGateway || '').trim();
    if (!gateway) {
      setError(t('parentFees.errors.selectMethod'));
      return;
    }

    const amountNum = payAmount != null && String(payAmount).trim() !== '' ? Number(payAmount) : NaN;
    if (!Number.isFinite(amountNum) || !(amountNum > 0)) {
      setError(t('parentFees.errors.invalidAmount'));
      return;
    }

    if ((gateway === 'mtn' || gateway === 'orange') && !String(payMsisdn || '').trim()) {
      setError(t('parentFees.errors.phoneRequired'));
      return;
    }
    if (gateway === 'stripe' && !String(payEmail || '').trim()) {
      setError(t('parentFees.errors.emailRequired'));
      return;
    }

    try {
      setPayBusy(true);
      setError(null);

      if (methodAvailability && !visibleGateways.includes(gateway)) {
        throw new Error(t('parentFees.errors.methodNotAvailable'));
      }

      const body = {
        feeStructureId: feeStructureIdNum,
        gateway,
        amount: amountNum,
        msisdn: String(payMsisdn || '').trim() || undefined,
        email: String(payEmail || '').trim() || undefined
      };
      if (gateway === 'bank') {
        body.bankReference = String(payBankRef || '').trim() || undefined;
      }

      const resp = await fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/fees/pay`, {
        method: 'POST',
        body: JSON.stringify(body)
      });

      const redirectUrl = resp?.data?.redirectUrl;
      setPayOpen(false);

      await loadFees(selectedChild);

      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (e) {
      setError(e?.message || t('parentFees.errors.payFailed'));
    } finally {
      setPayBusy(false);
    }
  };

  const openReceipt = () => {
    setError(null);
    const unpaid = (feeStructures || [])
      .map(fs => ({ feeStructureId: fs.id, name: fs.name, outstanding: getFeeOutstanding(fs) }))
      .filter(x => x.outstanding > 0)
      .map(x => ({ ...x, selected: false, amount: String(x.outstanding) }));
    setReceiptItems(unpaid);
    setReceiptPayer('');
    setReceiptRef('');
    setReceiptNotes('');
    setReceiptFile(null);
    setReceiptOpen(true);
  };

  const toggleReceiptItem = (id) => {
    setReceiptItems(prev => prev.map(it => String(it.feeStructureId) === String(id) ? { ...it, selected: !it.selected } : it));
  };
  const setReceiptItemAmount = (id, val) => {
    setReceiptItems(prev => prev.map(it => String(it.feeStructureId) === String(id) ? { ...it, amount: val } : it));
  };
  const receiptTotal = receiptItems.filter(it => it.selected).reduce((s, it) => s + (Number(it.amount) || 0), 0);

  const submitReceipt = async () => {
    if (!selectedChild) return;
    const chosen = receiptItems.filter(it => it.selected && Number(it.amount) > 0);
    if (chosen.length === 0) { setError(t('parentFees.receipt.errors.selectFee', 'Select at least one fee and enter an amount.')); return; }
    if (!receiptFile) { setError(t('parentFees.receipt.errors.proofRequired', 'Please attach a proof of payment (screenshot or PDF).')); return; }
    try {
      setReceiptBusy(true);
      setError(null);
      const fd = new FormData();
      fd.append('items', JSON.stringify(chosen.map(it => ({ feeStructureId: Number(it.feeStructureId), amount: Number(it.amount) }))));
      if (receiptPayer) fd.append('payerName', receiptPayer);
      if (receiptRef) fd.append('reference', receiptRef);
      if (receiptNotes) fd.append('notes', receiptNotes);
      fd.append('proof', receiptFile);
      await fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/fees/submit-offline-payment`, { method: 'POST', body: fd });
      setReceiptOpen(false);
      await loadFees(selectedChild);
    } catch (e) {
      setError(e?.message || t('parentFees.receipt.errors.submitFailed', 'Failed to submit payment'));
    } finally {
      setReceiptBusy(false);
    }
  };

  const refreshPaymentStatus = async (paymentId) => {
    if (!selectedChild || !paymentId) return;
    try {
      setRefreshBusyId(paymentId);
      setError(null);
      await fetchJson(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/payments/${encodeURIComponent(paymentId)}/refresh-status`);
      await loadFees(selectedChild);
    } catch (e) {
      setError(e?.message || t('parentFees.errors.refreshFailed'));
    } finally {
      setRefreshBusyId(null);
    }
  };

  const buildApiUrl = (apiPath) => {
    const baseRaw = process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL || 'http://localhost:5000';
    const base = String(baseRaw || '').replace(/\/$/, '');
    const pathPart = apiPath.startsWith('/') ? apiPath : `/${apiPath}`;
    const baseEndsWithApi = /\/api$/i.test(base);
    const pathStartsWithApi = /^\/api(\b|\/)/i.test(pathPart);
    const cleanedPath = (baseEndsWithApi && pathStartsWithApi) ? pathPart.replace(/^\/api/i, '') : pathPart;
    return `${base}${cleanedPath}`.replace(/([^:]\/)\/+/, '$1');
  };

  const getAuthHeaders = () => {
    const headers = {};
    const tokenKeys = ['accessToken', 'token', 'authToken', 'jwt', 'id_token', 'access_token', 'ACCESS_TOKEN'];
    let token = null;
    for (const k of tokenKeys) {
      const v = localStorage.getItem(k) || sessionStorage.getItem(k);
      if (v) { token = v; break; }
    }
    if (token) {
      let tk = String(token).trim();
      if (tk.toLowerCase().startsWith('bearer ')) tk = tk.slice(7).trim();
      headers.Authorization = `Bearer ${tk}`;
    }
    const activePhase = localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase');
    if (activePhase) headers['X-Active-Phase'] = String(activePhase);
    const activePhaseId = localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId');
    if (activePhaseId) headers['X-Active-Phase-Id'] = String(activePhaseId);
    const scopedSchoolId = localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || localStorage.getItem('schoolId');
    if (scopedSchoolId) {
      headers['X-Scope-School-Id'] = String(scopedSchoolId);
      headers['X-School-Id'] = String(scopedSchoolId);
    }
    const ayKey = scopedSchoolId ? `${scopedSchoolId}:currentAcademicYearId` : 'currentAcademicYearId';
    const ayId = localStorage.getItem(ayKey) || localStorage.getItem('currentAcademicYearId');
    if (ayId) headers['X-Academic-Year-Id'] = String(ayId);
    return headers;
  };

  const openPaymentReceipt = async (paymentId, download) => {
    if (!selectedChild || !paymentId) return;
    try {
      setReceiptBusyId(paymentId);
      setError(null);
      const qs = download ? '?download=1' : '';
      const url = buildApiUrl(`/api/portal/parent/child/${encodeURIComponent(selectedChild)}/payments/${encodeURIComponent(paymentId)}/receipt${qs}`);
      const resp = await fetch(url, {
        method: 'GET',
        headers: {
          ...getAuthHeaders(),
          Accept: 'application/pdf'
        },
        credentials: 'include'
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(txt || t('parentFees.errors.receiptFailed'));
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      if (download) {
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = `receipt-${paymentId}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(objectUrl, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      setError(e?.message || t('parentFees.errors.receiptFailed'));
    } finally {
      setReceiptBusyId(null);
    }
  };

  useEffect(() => {
    let alive = true;
    fetchJson('/api/portal/parent/children')
      .then((data) => {
        if (!alive) return;
        const rows = Array.isArray(data?.children) ? data.children : [];
        setChildren(rows);
        if (rows.length > 0) setSelectedChild(rows[0].studentId || rows[0].id);
      })
      .catch((e) => { if (alive) setError(e?.message || t('parentFees.errors.loadChildren')); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    fetchJson('/api/portal/parent/payment-methods')
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res?.methods || res;
        const avail = data && typeof data === 'object'
          ? {
              mtn: !!data.mtn,
              stripe: !!data.stripe,
              orange: !!data.orange,
              bank: !!data.bank
            }
          : { mtn: false, stripe: false, orange: false, bank: false };
        setMethodAvailability(avail);
        if (res?.bankDetails || data?.bankDetails) {
          setBankDetails(res?.bankDetails || data?.bankDetails);
        }
        setPayGateway((prev) => {
          const p = String(prev || '').trim();
          if (p && avail[p]) return p;
          return pickDefaultGateway(avail);
        });
      })
      .catch(() => {
        if (!alive) return;
        setMethodAvailability({ mtn: false, stripe: false, orange: false, bank: false });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    if (!selectedChild) return () => { alive = false; };
    setError(null);
    loadFees(selectedChild)
      .then(() => { if (!alive) return; })
      .catch((e) => { if (alive) setError(e?.message || t('parentFees.errors.loadFees')); });
    return () => { alive = false; };
  }, [selectedChild]);

  const statusColor = (s) => {
    const st = String(s || '').toLowerCase();
    if (st === 'completed') return 'success';
    if (st === 'pending') return 'warning';
    if (st === 'failed' || st === 'cancelled') return 'error';
    return 'default';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" gutterBottom>{t('parentFees.title')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <ChildPicker
        children={children}
        value={selectedChild}
        onChange={setSelectedChild}
        label={t('parentFees.selectChild')}
        studentWord={t('parentFees.student')}
        sx={{ mb: 2 }}
      />

      {selectedChild && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">{t('parentFees.summary.totalExpected')}</Typography>
                  <Typography variant="h6">{Number(summary?.totalExpected || 0).toLocaleString()} XAF</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">{t('parentFees.summary.totalPaid')}</Typography>
                  <Typography variant="h6" color="success.main">{Number(summary?.totalPaid || 0).toLocaleString()} XAF</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary">{t('parentFees.summary.outstanding')}</Typography>
                  <Typography variant="h6" color={summary?.outstanding > 0 ? 'error.main' : 'text.primary'}>{Number(summary?.outstanding || 0).toLocaleString()} XAF</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="subtitle2">{t('parentFees.feeStructures')}</Typography>
            <Button size="small" variant="outlined" onClick={openReceipt} disabled={(summary?.outstanding || 0) <= 0}>
              {t('parentFees.receipt.openButton', 'Pay by bank / mobile money (upload receipt)')}
            </Button>
          </Box>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('parentFees.table.name')}</TableCell>
                <TableCell align="right">{t('parentFees.table.amount')}</TableCell>
                <TableCell align="right">{t('parentFees.table.paid')}</TableCell>
                <TableCell>{t('parentFees.table.dueDate')}</TableCell>
                <TableCell align="right">{t('parentFees.table.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {feeStructures.map(fs => {
                const paid = getFeePaidAmount(fs.id);
                const outstanding = getFeeOutstanding(fs);
                const complete = !(outstanding > 0);
                const installments = Array.isArray(fs.installments) ? fs.installments : [];
                const hasSchedule = installments.length > 0;
                const open = !!expandedFees[fs.id];
                return (
                <React.Fragment key={fs.id}>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {hasSchedule && (
                        <IconButton
                          size="small"
                          onClick={() => setExpandedFees(prev => ({ ...prev, [fs.id]: !prev[fs.id] }))}
                          aria-label={t('parentFees.installments.toggle', 'Show instalments')}
                        >
                          {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                        </IconButton>
                      )}
                      <Box>
                        <Typography variant="body2">{fs.name}</Typography>
                        {hasSchedule && (
                          <Typography variant="caption" color="text.secondary">
                            {t('parentFees.installments.progress', {
                              paid: Number(fs.installmentsPaidCount || 0),
                              total: installments.length,
                              defaultValue: '{{paid}} of {{total}} instalments paid'
                            })}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{Number(fs.amount || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">{Number(paid || 0).toLocaleString()}</TableCell>
                  <TableCell>{fs.dueDate ? new Date(fs.dueDate).toLocaleDateString() : ''}</TableCell>
                  <TableCell align="right">
                    {complete ? (
                      <Chip label={t('parentFees.paidInFull')} size="small" color="success" />
                    ) : (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={methodAvailability && visibleGateways.length === 0}
                        onClick={() => openPay(fs)}
                      >
                        {t('parentFees.pay')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>

                {/* Instalment schedule. Status is derived server-side by applying what
                    has been paid across the schedule in order, so approving a receipt
                    clears the earliest unpaid instalment first. */}
                {hasSchedule && (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ my: 1.5, ml: { xs: 0, sm: 4 } }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t('parentFees.installments.name', 'Instalment')}</TableCell>
                                <TableCell align="right">{t('parentFees.table.amount')}</TableCell>
                                <TableCell align="right">{t('parentFees.table.paid')}</TableCell>
                                <TableCell>{t('parentFees.table.dueDate')}</TableCell>
                                <TableCell align="right">{t('parentFees.installments.status', 'Status')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {installments.map(inst => (
                                <TableRow key={inst.id}>
                                  <TableCell>{inst.name}</TableCell>
                                  <TableCell align="right">{Number(inst.amount || 0).toLocaleString()}</TableCell>
                                  <TableCell align="right">{Number(inst.amountPaid || 0).toLocaleString()}</TableCell>
                                  <TableCell>{inst.dueAt ? new Date(inst.dueAt).toLocaleDateString() : ''}</TableCell>
                                  <TableCell align="right">
                                    <Chip
                                      size="small"
                                      label={t(`parentFees.installments.${inst.status}`, INSTALLMENT_STATUS_FALLBACK[inst.status] || inst.status)}
                                      color={INSTALLMENT_STATUS_COLOR[inst.status] || 'default'}
                                      variant={inst.status === 'paid' ? 'filled' : 'outlined'}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
                </React.Fragment>
                );
              })}
              {feeStructures.length === 0 && (
                <TableRow><TableCell colSpan={5}>{t('parentFees.noFeeStructures')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          <Typography variant="subtitle2" gutterBottom>{t('parentFees.paymentHistory')}</Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('parentFees.table.feeItem')}</TableCell>
                <TableCell align="right">{t('parentFees.table.amount')}</TableCell>
                <TableCell>{t('parentFees.table.status')}</TableCell>
                {/* No "Provider" column. Which gateway carried the money is the
                    school's bookkeeping, not the parent's — they know how they
                    paid — and it cost a column on a table that has to fit a
                    phone. `p.provider` is still read below to decide whether a
                    pending mobile-money payment can be re-checked. */}
                <TableCell>{t('parentFees.table.date')}</TableCell>
                <TableCell align="right">{t('parentFees.table.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map(p => {
                const provider = String(p.provider || '').toLowerCase();
                const status = String(p.status || '').toLowerCase();
                const canRefresh = (provider === 'mtn_momo' || provider === 'orange_money') && status === 'pending';
                const canReceipt = status === 'completed';
                const feeName = (feeStructures || []).find(fs => String(fs.id) === String(p.feeStructureId))?.name || '';
                const displayDate = p.updatedAt || p.createdAt;
                return (
                <TableRow key={p.id}>
                  <TableCell>{feeName}</TableCell>
                  <TableCell align="right">{Number(p.amount || 0).toLocaleString()}</TableCell>
                  <TableCell>
                    <Chip label={t(`parentFees.statuses.${status}`, status)} size="small" color={statusColor(status)} />
                    {(status === 'cancelled' || status === 'failed') && p.failureReason ? (
                      <Typography variant="caption" color="error" display="block">{p.failureReason}</Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{displayDate ? new Date(displayDate).toLocaleString() : ''}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                      {p.proofUrl ? (
                        <Button size="small" variant="text" component="a" href={buildApiUrl(p.proofUrl)} target="_blank" rel="noopener">
                          {t('parentFees.receipt.viewProof', 'Proof')}
                        </Button>
                      ) : null}
                      {canRefresh ? (
                        <Button size="small" variant="outlined" disabled={String(refreshBusyId) === String(p.id)} onClick={() => refreshPaymentStatus(p.id)}>
                          {t('parentFees.refresh')}
                        </Button>
                      ) : null}
                      {canReceipt ? (
                        <>
                          <Button size="small" variant="outlined" disabled={String(receiptBusyId) === String(p.id)} onClick={() => openPaymentReceipt(p.id, false)}>
                            {t('parentFees.receiptButton', 'View Receipt')}
                          </Button>
                          <Button size="small" variant="contained" disabled={String(receiptBusyId) === String(p.id)} onClick={() => openPaymentReceipt(p.id, true)}>
                            {t('parentFees.download')}
                          </Button>
                        </>
                      ) : null}
                    </Box>
                  </TableCell>
                </TableRow>
                );
              })}
              {payments.length === 0 && (
                <TableRow><TableCell colSpan={5}>{t('parentFees.noPayments')}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>

          {invoices.length > 0 && (
            <>
              <Typography variant="subtitle2" gutterBottom>{t('parentFees.invoices')}</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('parentFees.table.invoiceId')}</TableCell>
                    <TableCell align="right">{t('parentFees.table.amount')}</TableCell>
                    <TableCell>{t('parentFees.table.status')}</TableCell>
                    <TableCell>{t('parentFees.table.dueDate')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map(inv => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.id}</TableCell>
                      <TableCell align="right">{Number(inv.amount || 0).toLocaleString()}</TableCell>
                      <TableCell><Chip label={t(`parentFees.statuses.${String(inv.status || '').toLowerCase()}`, inv.status)} size="small" color={statusColor(inv.status)} /></TableCell>
                      <TableCell>{inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          <Dialog open={payOpen} onClose={() => setPayOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>{t('parentFees.dialog.title')}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('parentFees.dialog.feeItem')}</InputLabel>
                  <Select value={payFeeStructureId} label={t('parentFees.dialog.feeItem')} onChange={(e) => setPayFeeStructureId(e.target.value)}>
                    {feeStructures.map(fs => (
                      <MenuItem key={fs.id} value={String(fs.id)}>
                        {fs.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('parentFees.dialog.paymentMethod')}</InputLabel>
                  <Select value={payGateway} label={t('parentFees.dialog.paymentMethod')} onChange={(e) => setPayGateway(e.target.value)}>
                    {!methodAvailability && (
                      <MenuItem value="" disabled>{t('parentFees.dialog.loadingMethods')}</MenuItem>
                    )}
                    {methodAvailability && visibleGateways.length === 0 && (
                      <MenuItem value="" disabled>{t('parentFees.dialog.noMethods')}</MenuItem>
                    )}
                    {methodAvailability?.mtn && (
                      <MenuItem value="mtn">{t('parentFees.methods.mtnMobileMoney')}</MenuItem>
                    )}
                    {methodAvailability?.orange && (
                      <MenuItem value="orange">{t('parentFees.methods.orangeMoney')}</MenuItem>
                    )}
                    {methodAvailability?.stripe && (
                      <MenuItem value="stripe">{t('parentFees.methods.bankCard')}</MenuItem>
                    )}
                    {methodAvailability?.bank && (
                      <MenuItem value="bank">{t('parentFees.methods.bankTransfer')}</MenuItem>
                    )}
                  </Select>
                </FormControl>

                <TextField label={t('parentFees.dialog.amount')} value={payAmount} onChange={(e) => setPayAmount(e.target.value)} fullWidth type="number" />

                {(payGateway === 'mtn' || payGateway === 'orange') && (
                  <TextField label={t('parentFees.dialog.phone')} value={payMsisdn} onChange={(e) => setPayMsisdn(e.target.value)} fullWidth />
                )}
                {payGateway === 'stripe' && (
                  <TextField label={t('parentFees.dialog.email')} value={payEmail} onChange={(e) => setPayEmail(e.target.value)} fullWidth />
                )}

                {payGateway === 'bank' && bankDetails && (
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>{t('parentFees.dialog.bankDetailsTitle')}</Typography>
                    <Divider sx={{ mb: 1 }} />
                    {bankDetails.bankName && <Typography variant="body2"><strong>{t('parentFees.dialog.bankName')}:</strong> {bankDetails.bankName}</Typography>}
                    {bankDetails.bankAccountName && <Typography variant="body2"><strong>{t('parentFees.dialog.accountName')}:</strong> {bankDetails.bankAccountName}</Typography>}
                    {bankDetails.bankAccountNumber && <Typography variant="body2"><strong>{t('parentFees.dialog.accountNumber')}:</strong> {bankDetails.bankAccountNumber}</Typography>}
                    {bankDetails.bankBranch && <Typography variant="body2"><strong>{t('parentFees.dialog.branch')}:</strong> {bankDetails.bankBranch}</Typography>}
                    {bankDetails.bankIban && <Typography variant="body2"><strong>{t('parentFees.dialog.iban')}:</strong> {bankDetails.bankIban}</Typography>}
                    {bankDetails.bankSwift && <Typography variant="body2"><strong>{t('parentFees.dialog.swift')}:</strong> {bankDetails.bankSwift}</Typography>}
                  </Box>
                )}

                {payGateway === 'bank' && (
                  <TextField label={t('parentFees.dialog.bankReference')} value={payBankRef} onChange={(e) => setPayBankRef(e.target.value)} fullWidth helperText={t('parentFees.dialog.bankReferenceHelper')} />
                )}

                {(payGateway === 'mtn' || payGateway === 'orange') && (
                  <Alert severity="info">{t('parentFees.dialog.momoInfo')}</Alert>
                )}
                {payGateway === 'bank' && (
                  <Alert severity="info">{t('parentFees.dialog.bankInfo')}</Alert>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPayOpen(false)} disabled={payBusy}>{t('parentFees.cancel')}</Button>
              <Button variant="contained" onClick={submitPay} disabled={payBusy || (methodAvailability && visibleGateways.length === 0)}>
                {payGateway === 'bank' ? t('parentFees.recordTransfer') : t('parentFees.pay')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Offline receipt submission: pay one or more fees by bank/mobile money + upload proof */}
          <Dialog open={receiptOpen} onClose={() => setReceiptOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>{t('parentFees.receipt.title', 'Submit Payment Receipt')}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {bankDetails && (
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="subtitle2" gutterBottom>{t('parentFees.receipt.whereToPay', 'Where to pay')}</Typography>
                    <Divider sx={{ mb: 1 }} />
                    {bankDetails.bankName && <Typography variant="body2"><strong>{t('parentFees.dialog.bankName')}:</strong> {bankDetails.bankName}</Typography>}
                    {bankDetails.bankAccountName && <Typography variant="body2"><strong>{t('parentFees.dialog.accountName')}:</strong> {bankDetails.bankAccountName}</Typography>}
                    {bankDetails.bankAccountNumber && <Typography variant="body2"><strong>{t('parentFees.dialog.accountNumber')}:</strong> {bankDetails.bankAccountNumber}</Typography>}
                    {bankDetails.bankBranch && <Typography variant="body2"><strong>{t('parentFees.dialog.branch')}:</strong> {bankDetails.bankBranch}</Typography>}
                    {bankDetails.mtnNumber && <Typography variant="body2"><strong>{t('parentFees.receipt.mtn', 'MTN MoMo')}:</strong> {bankDetails.mtnNumber}</Typography>}
                    {bankDetails.orangeNumber && <Typography variant="body2"><strong>{t('parentFees.receipt.orange', 'Orange Money')}:</strong> {bankDetails.orangeNumber}</Typography>}
                  </Box>
                )}

                <Typography variant="subtitle2">{t('parentFees.receipt.selectFees', 'Select the fees you paid')}</Typography>
                {receiptItems.length === 0 ? (
                  <Alert severity="info">{t('parentFees.receipt.noOutstanding', 'No outstanding fees to pay.')}</Alert>
                ) : receiptItems.map(it => (
                  <Box key={it.feeStructureId} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel
                      sx={{ flex: 1, mr: 0 }}
                      control={<Checkbox checked={it.selected} onChange={() => toggleReceiptItem(it.feeStructureId)} />}
                      label={`${it.name} (${Number(it.outstanding).toLocaleString()} XAF)`}
                    />
                    <TextField
                      size="small"
                      type="number"
                      sx={{ width: 130 }}
                      label={t('parentFees.dialog.amount')}
                      value={it.amount}
                      disabled={!it.selected}
                      onChange={(e) => setReceiptItemAmount(it.feeStructureId, e.target.value)}
                    />
                  </Box>
                ))}

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">{t('parentFees.receipt.total', 'Total')}</Typography>
                  <Typography variant="subtitle2">{Number(receiptTotal).toLocaleString()} XAF</Typography>
                </Box>

                <TextField label={t('parentFees.receipt.payerName', 'Payer name')} value={receiptPayer} onChange={(e) => setReceiptPayer(e.target.value)} fullWidth />
                <TextField label={t('parentFees.receipt.reference', 'Transaction reference')} value={receiptRef} onChange={(e) => setReceiptRef(e.target.value)} fullWidth />
                <TextField label={t('parentFees.receipt.notes', 'Notes (optional)')} value={receiptNotes} onChange={(e) => setReceiptNotes(e.target.value)} fullWidth multiline minRows={2} />

                <Button component="label" variant="outlined">
                  {receiptFile ? t('parentFees.receipt.changeProof', 'Change proof file') : t('parentFees.receipt.attachProof', 'Attach proof of payment')}
                  <input type="file" hidden accept="image/*,application/pdf" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
                </Button>
                {receiptFile && <Typography variant="caption" color="text.secondary">{receiptFile.name}</Typography>}

                <Alert severity="info">{t('parentFees.receipt.info', 'Your payment will be reviewed by the school. Once approved, it will be recorded against the selected fees.')}</Alert>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReceiptOpen(false)} disabled={receiptBusy}>{t('parentFees.cancel')}</Button>
              <Button variant="contained" onClick={submitReceipt} disabled={receiptBusy || receiptItems.length === 0}>
                {receiptBusy ? t('parentFees.receipt.submitting', 'Submitting...') : t('parentFees.receipt.submit', 'Submit for Review')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
}
